/**
 * The service worker's decisions, as plain functions (sw.ts does the work; apps/web/test/pwa.test.ts
 * checks these): which requests it may answer from its cache, what a notification shows, and which
 * Stitch window a tapped notification opens in.
 */

/** How the service worker treats a request. */
export type Route =
  /** Never touched, never kept: the API (and anything not ours or not a GET). */
  | 'pass'
  /** A page: from the network, the offline page when there's none. */
  | 'page'
  /** A fingerprinted file (/assets/…): from the cache once fetched; they never change. */
  | 'asset'
  /** One of the app shell's own files (icons, the offline page): kept with each version. */
  | 'shell';

/** Server addresses that aren't pages of the app, even when a browser opens them. */
const SERVER_ONLY = /^\/(api|hooks|mms)(\/|$)/;

export function routeFor(req: { method: string; url: string; mode: string }, origin: string, shell: ReadonlySet<string>): Route {
  if (req.method !== 'GET') return 'pass';
  let u: URL;
  try {
    u = new URL(req.url);
  } catch {
    return 'pass';
  }
  if (u.origin !== origin) return 'pass';
  // The API is never answered from a cache: what it says is always current, and private.
  if (SERVER_ONLY.test(u.pathname)) return 'pass';
  if (req.mode === 'navigate') return 'page';
  if (u.search) return 'pass';
  if (u.pathname.startsWith('/assets/')) return 'asset';
  if (shell.has(u.pathname)) return 'shell';
  return 'pass';
}

/** A path in Stitch to open: never another site, whatever a notification says. */
export function safeUrl(url: unknown): string {
  if (typeof url !== 'string' || url.length > 1000 || !url.startsWith('/') || url.startsWith('//') || /[\\\s\u0000-\u001f\u007f]/.test(url)) return '/';
  try {
    const u = new URL(url, 'https://stitch.invalid');
    return u.origin === 'https://stitch.invalid' ? `${u.pathname}${u.search}${u.hash}` : '/';
  } catch {
    return '/';
  }
}

/** What the API sends (apps/api/src/push/service.ts WirePayload), read with care. */
export interface PushData {
  kind: string;
  title: string;
  body: string;
  url: string;
  tag?: string;
  tenant?: string;
  conversationId?: string;
  at?: number;
  /** Ringing (a call, a Patch from a direct message): buzzes like a call. */
  ring?: boolean;
  /** Replaces the notification with the same tag without a sound (a ring answered elsewhere, 1.13.0). */
  quiet?: boolean;
}

const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');
const token = (v: unknown, max: number) => (typeof v === 'string' && v.length <= max && /^[A-Za-z0-9:._@-]+$/.test(v) ? v : undefined);

export function readPush(data: unknown): PushData {
  const d = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const tag = token(d.tag, 64);
  const tenant = token(d.tenant, 253);
  const conversationId = token(d.conversationId, 100);
  return {
    kind: token(d.kind, 20) ?? 'message',
    title: str(d.title, 120).trim() || 'Stitch',
    body: str(d.body, 600) || (d.title ? '' : 'You have a new notification.'),
    url: safeUrl(d.url),
    ...(tag ? { tag } : {}),
    ...(tenant ? { tenant } : {}),
    ...(conversationId ? { conversationId } : {}),
    ...(typeof d.at === 'number' && Number.isFinite(d.at) ? { at: d.at } : {}),
    ...(d.ring === true ? { ring: true } : {}),
    ...(d.quiet === true && d.ring !== true ? { quiet: true } : {})
  };
}

/** The options for showNotification(): the same tag replaces the last one (and still alerts). */
export function notificationFor(data: unknown, art: { icon: string; badge: string }): { title: string; options: NotificationOptions & { renotify?: boolean; timestamp?: number; vibrate?: number[] } } {
  const p = readPush(data);
  return {
    title: p.title,
    options: {
      body: p.body,
      icon: art.icon,
      // Android's status bar: the S, in one colour.
      badge: art.badge,
      ...(p.tag ? { tag: p.tag, renotify: !p.quiet } : {}),
      ...(p.quiet ? { silent: true } : {}),
      ...(p.at ? { timestamp: p.at } : {}),
      // A call (or a Patch) ringing: buzzes like one, on phones that buzz. A missed one doesn't.
      ...(p.ring ? { vibrate: [400, 200, 400, 200, 400] } : {}),
      data: { url: p.url, kind: p.kind, ...(p.ring ? { ring: true } : {}), ...(p.tenant ? { tenant: p.tenant } : {}), ...(p.conversationId ? { conversationId: p.conversationId } : {}) }
    }
  };
}

/**
 * A push about a message whose notification is already showing (the open page showed it when the
 * message came; the push follows a minute on if it's still unread): replace it without a second alert.
 */
export function alreadyShown(url: unknown, shown: readonly { data?: unknown }[]): boolean {
  return typeof url === 'string' && url.length > 0 && shown.some((n) => (n.data as { url?: unknown } | null | undefined)?.url === url);
}

/** Windows that aren't for working in: a wallboard on a TV, live help's viewer, a session replay. */
const NOT_FOR_WORK = /^\/(tv|live|replay)(\/|$)/;

/**
 * The Stitch window a tapped notification should open in: the one in front, else one on screen,
 * else any; never a TV wallboard or live help's viewer. -1: open a new one.
 */
export function pickClient(clients: readonly { url: string; focused: boolean; visible: boolean }[], origin: string): number {
  const ok = clients.map((c) => {
    try {
      const u = new URL(c.url);
      return u.origin === origin && !NOT_FOR_WORK.test(u.pathname);
    } catch {
      return false;
    }
  });
  const first = (test: (c: { focused: boolean; visible: boolean }) => boolean) => clients.findIndex((c, i) => ok[i] && test(c));
  const focused = first((c) => c.focused);
  if (focused >= 0) return focused;
  const visible = first((c) => c.visible);
  if (visible >= 0) return visible;
  return first(() => true);
}
